import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    IconButton,
    Checkbox,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    AlertTitle,
    TablePagination
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PayrollService } from '../../services/payroll.service';
import { IPayroll, IExcludedEmployee, PayrollExclusionReason, IPayrollRunTotals } from '../../interfaces/payroll.interface';
import { Segment } from '../../interfaces/accounting.interface';
import { SegmentService } from '../../services/segment.service';
import { useToast } from '../../context/ToastContext';
import PayrollAccrualChip from './PayrollAccrualChip';
import { usePermission } from '../../hooks/usePermission';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { displayUsername } from '../../utils/userUtils';
import { formatMoney, humanizeEnum, payslipFileBase } from '../../utils/format';
import { PERMISSIONS } from '../../constants/permissions';
import BatchPaymentDialog, { BatchPayrollRow } from './BatchPaymentDialog';
import SearchableSelect from '../common/SearchableSelect';
import PayrollApprovalBanner from './PayrollApprovalBanner';
import ExcludedEmployeesList from './ExcludedEmployeesList';
import { helpWhy } from '../../help/why';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

// Fix round 1 / H2 (D9): human-readable label for each backend exclusion reason.
const EXCLUSION_REASON_LABELS: Record<PayrollExclusionReason, string> = {
    MEMBERSHIP_SOFT_DELETED: 'membership removed',
    USER_DEACTIVATED: 'user deactivated',
    USER_SOFT_DELETED: 'user removed',
    EXITED_BEFORE_PERIOD: 'exited before this period',
    PIECE_RATE_WITH_OVERTIME_ENABLED: 'piece-rate wage mode while overtime pay is enabled',
    JOINED_AFTER_PERIOD: 'joined after this period',
    EXIT_BEFORE_JOINING: 'last working day is before the joining date',
    NO_ACTIVE_SALARY: 'no active salary record - add a salary so they are paid'
};

const formatExclusionReason = (reason: PayrollExclusionReason): string =>
    EXCLUSION_REASON_LABELS[reason] ?? reason;

/** Phone width keeps Employee, Net and Status; the breakdown columns come back from md up (board DA-03). */
const hideOnPhone = { display: { xs: 'none', md: 'table-cell' } };

const isValidYear = (y: number) => Number.isInteger(y) && y >= 1000 && y <= 9999;
const isValidMonth = (m: number) => Number.isInteger(m) && m >= 1 && m <= 12;

/**
 * Period shown for a URL. A valid ?month= with no usable ?year= means "that month of the current year"; with
 * neither, the most recent month that can have a run (last month). Anything invalid falls back the same way.
 */
const periodFromParams = (params: URLSearchParams, now: Date): { year: number; month: number } => {
    const y = parseInt(params.get('year') ?? '', 10);
    const m = parseInt(params.get('month') ?? '', 10);
    const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const month = isValidMonth(m) ? m : last.getMonth() + 1;
    const year = isValidYear(y) ? y : (isValidMonth(m) ? now.getFullYear() : last.getFullYear());
    return { year, month };
};

const PayrollList: React.FC = () => {
    const now = new Date();
    // B-41: the period lives in the URL (?year=2026&month=9) so the screen can be deep-linked.
    const [searchParams, setSearchParams] = useSearchParams();
    const [year, setYear] = useState(() => periodFromParams(searchParams, now).year);
    const [month, setMonth] = useState(() => periodFromParams(searchParams, now).month);
    // Latest period on screen, so the URL-sync effect below can tell a real change from an echo of our own write.
    const periodRef = useRef({ year, month });
    periodRef.current = { year, month };
    const [payrolls, setPayrolls] = useState<IPayroll[]>([]);
    const [loaded, setLoaded] = useState(false);
    // Server-side paging: `payrolls` is ONE page; `totalElements` and `totals` describe the WHOLE run.
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [totalElements, setTotalElements] = useState(0);
    const [totals, setTotals] = useState<IPayrollRunTotals | null>(null);
    const [totalsFailed, setTotalsFailed] = useState(false);
    // Count returned by the server for "select all unpaid finalized"; null until that button is used.
    const [runSelectionCount, setRunSelectionCount] = useState<number | null>(null);
    // Selection survives page changes (keyed by payroll id) and can span the whole run via
    // "Select all N in run", so it carries what the batch-payment dialog needs for each row.
    const [selected, setSelected] = useState<Map<string, BatchPayrollRow>>(new Map());
    const [selectingRun, setSelectingRun] = useState(false);
    const selectedIds = useMemo(() => new Set(selected.keys()), [selected]);
    const [batchDialogOpen, setBatchDialogOpen] = useState(false);
    // Segments are optional/non-blocking: a failed fetch leaves pickers showing only (default).
    const [segments, setSegments] = useState<Segment[]>([]);
    const [segmentMap, setSegmentMap] = useState<Map<string, Segment>>(new Map());
    const [bulkSegmentId, setBulkSegmentId] = useState<string>('');
    const [downloadingZip, setDownloadingZip] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [emailingAll, setEmailingAll] = useState(false);
    // Fix round 1 / L-1: the run-email endpoint now returns 202 immediately (T6 item 4), so
    // `emailingAll` alone only throttles a double-click during the brief accept round trip —
    // once the accepted response lands the button re-enables while the actual send is still
    // running in the background. This flag stays true until the next explicit Load, so a second
    // "Email all" on the same loaded run is blocked rather than enqueuing a duplicate render+mail
    // pass onto the shared async executor.
    const [emailRunAcceptedForLoadedRun, setEmailRunAcceptedForLoadedRun] = useState(false);
    const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
    const [voidingDraft, setVoidingDraft] = useState(false);
    const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
    const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false);
    // Fix round 1 / H2 (D9): every roster member the last generate run dropped, and why.
    // Cleared whenever the filters change or the alert is dismissed — it describes ONE run, not
    // a persistent state of the institution.
    const [exclusions, setExclusions] = useState<IExcludedEmployee[]>([]);
    // Board B-03: active members with no active salary (no payslip possible), refreshed on every load.
    const [noSalaryExcluded, setNoSalaryExcluded] = useState<IExcludedEmployee[]>([]);
    const { showSuccess, showError, showApiError } = useToast();
    const navigate = useNavigate();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const canCreatePayment = usePermission(PERMISSIONS.SALARY_PAYMENT_MANAGE);
    // B-46: mutating buttons are for PAYROLL_MANAGE holders; an approver without it would only hit a 403.
    const canManage = usePermission(PERMISSIONS.PAYROLL_MANAGE);
    // The backend also lets the accountant dashboard role email a run, so keep that button for them.
    const canViewAsAccountant = usePermission(PERMISSIONS.DASHBOARD_VIEW_ACCOUNTANT);
    const canEmailRun = canManage || canViewAsAccountant;

    // B-41: follow the URL when it changes under us (sidebar link, back/forward). Our own writes echo back
    // with the same period, so nothing is reset and there is no loop. Uses only stable setters.
    useEffect(() => {
        const p = periodFromParams(searchParams, new Date());
        if (p.year === periodRef.current.year && p.month === periodRef.current.month) return;
        setSelected(new Map());
        setLoaded(false);
        setPayrolls([]);
        setTotals(null);
        setTotalElements(0);
        setExclusions([]);
        setYear(p.year);
        setMonth(p.month);
    }, [searchParams]);

    // Non-fatal segment fetch on mount. Build a display map from the FULL list
    // (so a row pointing at an inactive segment still shows its code), and keep
    // the active subset for the picker options.
    useEffect(() => {
        const fetchSegments = async () => {
            try {
                const res = await SegmentService.getByInstitution();
                const all: Segment[] = res.content ?? [];
                const map = new Map<string, Segment>();
                all.forEach(s => { if (s.id) map.set(s.id, s); });
                setSegmentMap(map);
                setSegments(all.filter(s => s.active !== false));
            } catch (err) {
                console.error('Error fetching segments:', err);
            }
        };
        fetchSegments();
    }, []);

    const loadPayrolls = async (targetPage: number = page, targetSize: number = pageSize, keepSelection = false) => {
        if (!institutionId) return;
        try {
            // Whole-run totals come from a SQL aggregate; a failure there must not hide the rows.
            let totalsRejected = false;
            const [data, runTotals, noSalary] = await Promise.all([
                PayrollService.getPayrollList(year, month, targetPage, targetSize, institutionId),
                Promise.resolve(PayrollService.getRunTotals(year, month, institutionId)).catch(() => {
                    totalsRejected = true;
                    return null;
                }),
                // Non-fatal: the exclusion notice must never hide the run itself.
                Promise.resolve(PayrollService.getNoActiveSalaryExclusions(year, month, institutionId))
                    .catch(() => [] as IExcludedEmployee[]),
            ]);
            const total = data.totalElements ?? data.content.length;
            // A page past the end (rows removed meanwhile) comes back empty: jump to the last valid page.
            if (data.content.length === 0 && targetPage > 0 && total > 0) {
                await loadPayrolls(Math.max(0, Math.ceil(total / targetSize) - 1), targetSize, keepSelection);
                return;
            }
            setPayrolls(data.content);
            setTotalElements(total);
            setTotals(runTotals ?? null);
            setNoSalaryExcluded(noSalary ?? []);
            setTotalsFailed(totalsRejected);
            if (!keepSelection) setRunSelectionCount(null);
            setPage(targetPage);
            setPageSize(targetSize);
            setLoaded(true);
            if (!keepSelection) setSelected(new Map());
            // Fix round 1 / L-1: a fresh Load re-arms "Email all" for this run.
            setEmailRunAcceptedForLoadedRun(false);
        } catch (error) {
            showApiError(error, 'Failed to load payroll data');
        }
    };

    // Open straight on the selected month's payroll instead of an empty screen; reload whenever
    // the institution or period changes. (Filter handlers clear the old rows first.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadPayrolls(0); }, [institutionId, year, month]);

    const handleGenerate = async () => {
        try {
            const result = await PayrollService.generatePayroll(year, month, institutionId);
            setExclusions(result.exclusions);
            showSuccess(`Generated payroll for ${result.payrolls.length} employees`);
            const loanWarnings = result.loanWarnings ?? [];
            if (loanWarnings.length > 0) {
                showError(`${loanWarnings.length} employee(s) had their flat Loan Repayment deduction replaced by loan recovery: ${loanWarnings.slice(0, 3).join('; ')}${loanWarnings.length > 3 ? '...' : ''}`);
            }
            loadPayrolls(0);
        } catch (error) {
            showApiError(error, 'Failed to generate payroll');
        }
    };

    const handleFinalizeAll = async () => {
        setFinalizeConfirmOpen(false);
        try {
            const result = await PayrollService.finalizeAllPayroll(year, month, institutionId);
            showSuccess('All payrolls finalized');
            const warnings = result?.accrualWarnings ?? [];
            if (warnings.length > 0) {
                showError(`${warnings.length} payroll accrual(s) not posted: ${warnings[0].message}`
                    + (warnings.length > 1 ? ` (and ${warnings.length - 1} more; see the "Accrual not posted" chips)` : ''));
            }
            loadPayrolls();
        } catch (error) {
            showApiError(error, 'Failed to finalize payrolls');
        }
    };

    const handleFinalize = async (id: string) => {
        try {
            const result = await PayrollService.finalizePayroll(id);
            showSuccess('Payroll finalized');
            if (result?.accrualWarning) {
                showError(result.accrualWarning);
            }
            loadPayrolls();
        } catch (error) {
            showApiError(error, 'Failed to finalize payroll');
        }
    };

    const handleDownload = async (p: IPayroll) => {
        try {
            await PayrollService.downloadPayslip(p.id, undefined, payslipFileBase(p));
        } catch (error) {
            showApiError(error, 'Failed to download payslip');
        }
    };

    const handleDownloadAllZip = async () => {
        if (downloadingZip) return;
        setDownloadingZip(true);
        try {
            await PayrollService.downloadPayslipsZip(institutionId, year, month);
        } catch (error) {
            showApiError(error, 'Failed to download payslips ZIP');
        } finally {
            setDownloadingZip(false);
        }
    };

    const handleDownloadAllPdf = async () => {
        if (downloadingPdf) return;
        setDownloadingPdf(true);
        try {
            await PayrollService.downloadPayslipsCombinedPdf(institutionId, year, month);
        } catch (error) {
            showApiError(error, 'Failed to download combined payslip PDF');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const handleVoidDraftClick = () => {
        setVoidConfirmOpen(true);
    };

    const handleCancelVoidDraft = () => {
        setVoidConfirmOpen(false);
    };

    const handleConfirmVoidDraft = async () => {
        if (voidingDraft) return;
        setVoidConfirmOpen(false);
        setVoidingDraft(true);
        try {
            const res = await PayrollService.voidDraftMonth(year, month, institutionId);
            showSuccess(`Voided ${res.voidedCount} draft payroll(s)`);
            loadPayrolls();
        } catch (error) {
            showApiError(error, 'Failed to void draft payroll month');
        } finally {
            setVoidingDraft(false);
        }
    };

    const handleEmailAllClick = () => {
        setEmailConfirmOpen(true);
    };

    const handleCancelEmailAll = () => {
        setEmailConfirmOpen(false);
    };

    const handleConfirmEmailAll = async () => {
        if (emailingAll || emailRunAcceptedForLoadedRun) return;
        setEmailConfirmOpen(false);
        setEmailingAll(true);
        try {
            // T6 item 4: rendering + sending now happens off the request thread, so only a
            // candidate count is known synchronously — final sent/skipped totals are no longer
            // available in the response.
            const res = await PayrollService.emailRunPayslips(institutionId, year, month);
            showSuccess(`Emailing ${res.candidateCount} payslip(s) — this may take a moment`);
            // Fix round 1 / L-1: block a second run against this loaded run until Load is
            // clicked again, rather than letting an impatient re-click enqueue a duplicate
            // render+mail pass.
            setEmailRunAcceptedForLoadedRun(true);
            // Fix round 1 / L-6: no refresh here — the accepted response lands before a single
            // payslipEmailedAt could have been written by the background run, so an immediate
            // reload was a guaranteed no-op that implied completion. The next explicit Load
            // (which also re-arms "Email all", above) is when the grid should reflect it.
        } catch (error) {
            showApiError(error, 'Failed to email payslips');
        } finally {
            setEmailingAll(false);
        }
    };

    const toBatchRow = (p: IPayroll): BatchPayrollRow => ({
        id: p.id,
        employeeName: p.employeeName || displayUsername(p.userName),
        netSalary: p.netSalary,
        // NOTE: banking fields are not currently exposed on PayrollDTO;
        // preview will degrade gracefully (see BatchPaymentDialog).
        user: undefined,
    });

    const handleToggleSelect = (p: IPayroll) => {
        setSelected(prev => {
            const next = new Map(prev);
            if (next.has(p.id)) next.delete(p.id); else next.set(p.id, toBatchRow(p));
            return next;
        });
    };

    // The header checkbox acts on the rows of the CURRENT page; "Select all N in run" (below)
    // extends the selection to the whole run.
    const finalizedOnPage = payrolls.filter(p => p.status === 'FINALIZED');
    const finalizedIds = finalizedOnPage.map(p => p.id);
    const allFinalizedSelected = finalizedIds.length > 0 && finalizedIds.every(id => selectedIds.has(id));
    const someFinalizedSelected = finalizedIds.some(id => selectedIds.has(id)) && !allFinalizedSelected;

    const handleToggleSelectAll = () => {
        setSelected(prev => {
            const next = new Map(prev);
            if (allFinalizedSelected) {
                finalizedIds.forEach(id => next.delete(id));
            } else {
                finalizedOnPage.forEach(p => next.set(p.id, toBatchRow(p)));
            }
            return next;
        });
    };

    // Resolve "every FINALIZED payroll of the run" server-side, so the selection (and the bulk
    // action that follows) reaches rows on pages that were never loaded.
    const handleSelectWholeRun = async () => {
        if (selectingRun || !institutionId) return;
        setSelectingRun(true);
        try {
            const rows = await PayrollService.getFinalizedRunSelection(year, month, institutionId);
            setRunSelectionCount(rows.length);
            setSelected(new Map(rows.map(r => [r.id, {
                id: r.id,
                employeeName: r.employeeName,
                netSalary: r.netSalary,
                user: undefined,
            } as BatchPayrollRow])));
        } catch (error) {
            showApiError(error, 'Failed to select all payrolls in the run');
        } finally {
            setSelectingRun(false);
        }
    };

    const selectedBatchRows: BatchPayrollRow[] = Array.from(selected.values());

    const handleBatchSuccess = () => {
        setSelected(new Map());
        loadPayrolls();
    };

    // Per-row inline segment override. Backend is the authoritative paid-guard:
    // on any error (e.g. truly-paid row), surface the message and leave row unchanged.
    const handleRowSegmentChange = async (id: string, value: string) => {
        try {
            const updated = await PayrollService.updatePayrollSegment(id, value || undefined);
            setPayrolls(prev => prev.map(p =>
                p.id === id ? { ...p, segmentId: updated?.segmentId ?? (value || undefined) } : p
            ));
        } catch (error) {
            showApiError(error, 'Failed to update segment');
        }
    };

    // Bulk set segment for the currently CHECKED rows. Atomic on the backend:
    // a 4xx reject changes nothing, so we surface the message and do not mutate state.
    const handleBulkSegment = async () => {
        const checkedIds = Array.from(selectedIds);
        if (checkedIds.length === 0) return;
        try {
            await PayrollService.bulkUpdatePayrollSegment(checkedIds, bulkSegmentId || undefined);
            showSuccess(`Segment updated for ${checkedIds.length} payroll(s)`);
            loadPayrolls();
        } catch (error) {
            showApiError(error, 'Failed to update segment for selected payrolls');
        }
    };

    // Clear selections whenever any filter changes so stale selections don't
    // survive into a different year/month/institution context. C3: also drop the
    // previously loaded run (`loaded`/`payrolls`) — otherwise the "Payslips for this
    // run" bulk block (ZIP/PDF/Email all) stays visible and its handlers read the
    // NEW filter values live from state while still describing the OLD on-screen
    // table, so "Email all" can fire against a run that was never loaded/confirmed.
    const handleYearChange = (val: number) => {
        setSelected(new Map());
        setLoaded(false);
        setPayrolls([]);
        setTotals(null);
        setTotalElements(0);
        setExclusions([]);
        setYear(val);
        if (isValidYear(val)) setSearchParams({ year: String(val), month: String(month) });
    };
    const handleMonthChange = (val: number) => {
        setSelected(new Map());
        setLoaded(false);
        setPayrolls([]);
        setTotals(null);
        setTotalElements(0);
        setExclusions([]);
        setMonth(val);
        if (isValidYear(year) && isValidMonth(val)) setSearchParams({ year: String(year), month: String(val) });
    };
    const handleInstitutionChange = (val: string) => {
        setSelected(new Map());
        setLoaded(false);
        setPayrolls([]);
        setTotals(null);
        setTotalElements(0);
        setExclusions([]);
        setInstitutionId(val);
    };

    const selectedInstitutionName = institutions.find(inst => inst.id === institutionId)?.name;

    // Run-level facts (draft/finalized counts, overtime column) come from the whole-run aggregate
    // so they stay right when the rows of interest are on another page; the loaded page is only a
    // fallback if the aggregate could not be fetched.
    const draftCount = totals ? totals.draftCount : payrolls.filter(p => p.status === 'DRAFT').length;
    const finalizedCount = totals ? totals.finalizedCount : payrolls.filter(p => p.status === 'FINALIZED').length;
    const hasDrafts = draftCount > 0;
    const hasFinalized = finalizedCount > 0;
    const hasOvertime = totals ? Number(totals.overtimeAmount) > 0 : payrolls.some(p => (p.overtimeAmount ?? 0) > 0);
    const monthNotOver = year * 12 + month >= now.getFullYear() * 12 + (now.getMonth() + 1);

    return (
        <Box data-help-section="payroll.run" data-help-page="payroll.run.page" sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>Monthly Payroll</Typography>

            <Box data-help-section="payroll.run.filter" sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                {isSystemAdmin && (
                    <SearchableSelect
                        data-help="payroll.run.institution"
                        label="Select Institution"
                        options={institutions}
                        value={institutionId}
                        onChange={id => handleInstitutionChange(id)}
                        getOptionLabel={inst => inst.name}
                        clearable={false}
                        sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    />
                )}
                <TextField
                    data-help="payroll.run.year"
                    label="Year"
                    type="number"
                    value={year}
                    onChange={(e) => handleYearChange(parseInt(e.target.value))}
                    size="small"
                    sx={{ flex: '1 1 100px', maxWidth: 140 }}
                />
                <TextField
                    data-help="payroll.run.month"
                    label="Month"
                    select
                    value={month}
                    onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                    size="small"
                    sx={{ flex: '1 1 130px', maxWidth: 190 }}
                    SelectProps={{ native: true }}
                >
                    {MONTHS.map((name, i) => (
                        <option key={i + 1} value={i + 1}>{name}</option>
                    ))}
                </TextField>
                <Button data-help="payroll.run.load" variant="outlined" onClick={() => loadPayrolls(0)} disabled={!institutionId}>Load</Button>
                {canManage && (
                    <Tooltip title={monthNotOver ? `${MONTHS[month - 1]} ${year} is not over yet. Generate payroll once the month has ended.` : ''}>
                        <span>
                            <Button data-help="payroll.generate" {...helpWhy(monthNotOver ? { kind: 'status', message: 'The month has not ended yet. Generate payroll after it ends.' } : null)} variant="contained" color="primary" onClick={handleGenerate} disabled={!institutionId || monthNotOver}>
                                Generate Payroll
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {canManage && hasDrafts && (
                    <Button data-help="payroll.finalizeAll" variant="contained" color="warning" onClick={() => setFinalizeConfirmOpen(true)}>
                        Finalize All
                    </Button>
                )}
                {canManage && hasDrafts && (
                    <Button
                        variant="outlined"
                        color="error"
                        data-help="payroll.voidDraft"
                        onClick={handleVoidDraftClick}
                        disabled={voidingDraft}
                    >
                        Void Draft Month
                    </Button>
                )}
                <Button
                    data-help="payroll.run.reconciliation"
                    variant="outlined"
                    onClick={() => navigate('/payroll/reconciliation')}
                    disabled={!institutionId}
                >
                    Reconciliation
                </Button>
                {canCreatePayment && selectedIds.size > 0 && (
                    <Button
                        data-help="payroll.run.bulkPay"
                        variant="contained"
                        color="success"
                        onClick={() => setBatchDialogOpen(true)}
                    >
                        Mark Paid (Bulk) [{selectedIds.size}]
                    </Button>
                )}
            </Box>

            {loaded && noSalaryExcluded.length > 0 && (
                // Board B-03: shown before any Finalize action so nobody is left unpaid unnoticed.
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="no-salary-excluded">
                    <AlertTitle>Excluded: {noSalaryExcluded.length}</AlertTitle>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Active employees with no active salary get no payslip in this run.
                    </Typography>
                    <ExcludedEmployeesList
                        employees={noSalaryExcluded}
                        reasonLabel={(ex) => formatExclusionReason(ex.reason)}
                        testId="no-salary-excluded-list"
                    />
                    {canManage && (
                        <Button size="small" onClick={() => navigate('/payroll/employee-salaries')} sx={{ mt: 0.5, ml: 2 }}>
                            Add salaries
                        </Button>
                    )}
                </Alert>
            )}

            {exclusions.filter(ex => ex.reason !== 'NO_ACTIVE_SALARY').length > 0 && (
                // Fix round 1 / H2 (D9): surface the exclusions the backend already computes
                // on generate — a dismissible list of who was skipped and why.
                <Alert severity="warning" onClose={() => setExclusions([])} sx={{ mb: 2 }}>
                    <AlertTitle>{exclusions.filter(ex => ex.reason !== 'NO_ACTIVE_SALARY').length} employee{exclusions.filter(ex => ex.reason !== 'NO_ACTIVE_SALARY').length === 1 ? '' : 's'} excluded from this run</AlertTitle>
                    <ExcludedEmployeesList
                        employees={exclusions.filter(ex => ex.reason !== 'NO_ACTIVE_SALARY')}
                        reasonLabel={(ex) => formatExclusionReason(ex.reason)}
                    />
                </Alert>
            )}

            {loaded && canManage && (
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <SearchableSelect
                        data-help="payroll.run.bulkSegment"
                        label="Bulk Segment"
                        placeholder="(default)"
                        options={segments}
                        value={bulkSegmentId}
                        onChange={id => setBulkSegmentId(id)}
                        getOptionLabel={s => `${s.code} — ${s.name}`}
                        sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    />
                    <Button
                        data-help="payroll.run.bulkSegmentApply"
                        variant="outlined"
                        onClick={handleBulkSegment}
                        disabled={selectedIds.size === 0}
                    >
                        Set segment for selected [{selectedIds.size}]
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 100%' }}>
                        Segment can only be changed on payrolls with no accrual journal or payment posted; finalized payrolls are usually locked and will be rejected.
                    </Typography>
                </Box>
            )}

            {loaded && hasFinalized && (
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" color="text.secondary">Payslips for this run:</Typography>
                    <Button
                        data-help="payroll.run.downloadZip"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadAllZip}
                        disabled={downloadingZip}
                    >
                        Download all (ZIP)
                    </Button>
                    <Button
                        data-help="payroll.run.downloadPdf"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadAllPdf}
                        disabled={downloadingPdf}
                    >
                        Download all (PDF)
                    </Button>
                    {canEmailRun && (
                        <Button
                            data-help="payroll.run.emailAll"
                            variant="outlined"
                            startIcon={<EmailIcon />}
                            onClick={handleEmailAllClick}
                            disabled={emailingAll || emailRunAcceptedForLoadedRun}
                        >
                            {emailRunAcceptedForLoadedRun ? 'Email run submitted' : 'Email all'}
                        </Button>
                    )}
                </Box>
            )}

            {loaded && institutionId && (
                <PayrollApprovalBanner institutionId={institutionId} year={year} month={month} refreshKey={totals} />
            )}

            {loaded && totals && totals.paymentStatus && (
                <Alert data-help="payroll.run.paymentStatus" severity={totals.paymentStatus === 'PAID' ? 'success' : 'warning'} sx={{ mb: 1 }}>
                    {totals.paymentStatus === 'PAID' ? 'Paid in full' : totals.paymentStatus === 'UNPAID' ? 'Unpaid' : 'Partly paid'}
                    : {formatMoney(totals.paidAmount ?? 0)} of {formatMoney(totals.payableNetTotal ?? 0)} paid
                    ({totals.paidCount ?? 0} of {totals.payableCount ?? 0} employees).
                </Alert>
            )}
            {loaded && totalsFailed && (
                <Alert severity="warning" sx={{ mb: 1 }} action={
                    <Button data-help="payroll.run.totalsRetry" color="inherit" size="small" onClick={() => loadPayrolls(page, pageSize, true)}>Retry</Button>
                }>
                    Run totals unavailable. The Total row and whole-run counts are hidden.
                </Alert>
            )}
            {loaded && !totalsFailed && finalizedCount > selected.size && allFinalizedSelected && runSelectionCount === null && (
                <Alert severity="info" sx={{ mb: 1 }} action={
                    <Button data-help="payroll.run.selectWholeRun" color="inherit" size="small" onClick={handleSelectWholeRun} disabled={selectingRun}>
                        {selectingRun ? 'Selecting...' : 'Select all unpaid finalized in this run'}
                    </Button>
                }>
                    {finalizedIds.length} finalized payroll{finalizedIds.length === 1 ? '' : 's'} on this page selected.
                    The run has {finalizedCount} finalized in total; already-paid rows are skipped.
                </Alert>
            )}
            {loaded && runSelectionCount !== null && selected.size === runSelectionCount && (
                <Alert severity="info" sx={{ mb: 1 }} action={
                    <Button data-help="payroll.run.clearSelection" color="inherit" size="small" onClick={() => { setSelected(new Map()); setRunSelectionCount(null); }}>Clear selection</Button>
                }>
                    All {runSelectionCount} unpaid finalized payroll{runSelectionCount === 1 ? '' : 's'} in this run are selected.
                </Alert>
            )}

            {loaded && (
                <TableContainer component={Paper} data-help-section="payroll.run.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" data-help="payroll.run.col.select">
                                    <Checkbox
                                        indeterminate={someFinalizedSelected}
                                        checked={allFinalizedSelected}
                                        onChange={handleToggleSelectAll}
                                        disabled={finalizedIds.length === 0}
                                        inputProps={{ 'aria-label': 'select all finalized' }}
                                    />
                                </TableCell>
                                <TableCell data-help="payroll.run.col.employee">Employee</TableCell>
                                <TableCell align="right" sx={hideOnPhone} data-help="payroll.run.col.gross">Gross</TableCell>
                                <TableCell align="right" sx={hideOnPhone} data-help="payroll.run.col.deductions">Deductions</TableCell>
                                <TableCell align="right" sx={hideOnPhone} data-help="payroll.run.col.leaveDed">Leave Ded.</TableCell>
                                <TableCell align="right" sx={hideOnPhone} data-help="payroll.run.col.absenceDed">Absence Ded.</TableCell>
                                {hasOvertime && <TableCell align="right" sx={hideOnPhone} data-help="payroll.run.col.overtime">Overtime</TableCell>}
                                <TableCell align="right" data-help="payroll.run.col.net">Net Salary</TableCell>
                                <TableCell data-help="payroll.run.col.status">Status</TableCell>
                                <TableCell data-help="payroll.run.col.segment">Segment</TableCell>
                                <TableCell align="right" data-help="payroll.run.col.actions">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {payrolls.map((p) => {
                                const selectable = p.status === 'FINALIZED';
                                // Conservative lock hint: FINALIZED rows may already be paid.
                                // The backend is the authoritative paid-guard and will reject
                                // a truly-paid edit with a toast even if a row is editable here.
                                const segmentLocked = p.status === 'FINALIZED';
                                return (
                                    <TableRow key={p.id}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                data-help="payroll.run.rowSelect"
                                                checked={selectedIds.has(p.id)}
                                                onChange={() => handleToggleSelect(p)}
                                                disabled={!selectable}
                                                inputProps={{ 'aria-label': `select payroll ${p.id}` }}
                                            />
                                        </TableCell>
                                        <TableCell>{p.employeeName || displayUsername(p.userName)}</TableCell>
                                        <TableCell align="right" sx={hideOnPhone}>{formatMoney(p.grossSalary)}</TableCell>
                                        <TableCell align="right" sx={hideOnPhone}>{formatMoney(p.totalDeductions)}</TableCell>
                                        <TableCell align="right" sx={hideOnPhone}>
                                            {p.unpaidLeaveDeduction > 0 ? `${formatMoney(p.unpaidLeaveDeduction)} (${p.unpaidLeaveDays}d)` : '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={hideOnPhone}>
                                            {p.absenceDeduction > 0 ? `${formatMoney(p.absenceDeduction)} (${p.absentDays}d)` : '-'}
                                        </TableCell>
                                        {hasOvertime && (
                                            <TableCell align="right" sx={hideOnPhone}>
                                                {(p.overtimeAmount ?? 0) > 0 ? formatMoney(p.overtimeAmount) : '-'}
                                            </TableCell>
                                        )}
                                        <TableCell align="right">{formatMoney(p.netSalary)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={humanizeEnum(p.status)}
                                                color={p.status === 'FINALIZED' ? 'success' : 'warning'}
                                                size="small"
                                            />{' '}
                                            <PayrollAccrualChip payroll={p} />
                                        </TableCell>
                                        <TableCell>
                                            {segmentLocked || !canManage ? (
                                                <Tooltip title={segmentLocked ? 'Locked — salary already posted' : ''}>
                                                    <span>
                                                        {p.segmentId
                                                            ? (segmentMap.has(p.segmentId)
                                                                ? `${segmentMap.get(p.segmentId)!.code} — ${segmentMap.get(p.segmentId)!.name}`
                                                                : '—')
                                                            : 'Common'}
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <SearchableSelect
                                                    data-help="payroll.run.rowSegment"
                                                    placeholder="(default)"
                                                    options={segments}
                                                    value={segments.some(s => s.id === p.segmentId) ? (p.segmentId ?? '') : ''}
                                                    onChange={id => handleRowSegmentChange(p.id, id)}
                                                    getOptionLabel={s => `${s.code} — ${s.name}`}
                                                    data-testid={`segment-select-${p.id}`}
                                                    sx={{ flex: '1 1 160px', maxWidth: 260 }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton data-help="payroll.run.action.show" onClick={() => navigate(`/payroll/run/${p.id}`)} size="small" aria-label="show">
                                                <VisibilityIcon />
                                            </IconButton>
                                            <IconButton data-help="payroll.run.action.download" onClick={() => handleDownload(p)} size="small" aria-label="download">
                                                <DownloadIcon />
                                            </IconButton>
                                            {canManage && p.status === 'DRAFT' && (
                                                <IconButton data-help="payroll.run.action.finalize" onClick={() => handleFinalize(p.id)} size="small" color="success" aria-label="finalize">
                                                    <CheckCircleIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {totals && totals.headcount > 0 && (
                                <TableRow sx={{ '& td': { fontWeight: 700 } }}>
                                    <TableCell padding="checkbox" />
                                    <TableCell data-help="payroll.run.totalRow">Total ({totals.headcount} employees)</TableCell>
                                    <TableCell align="right" sx={hideOnPhone}>{formatMoney(totals.grossSalary)}</TableCell>
                                    <TableCell align="right" sx={hideOnPhone}>{formatMoney(totals.totalDeductions)}</TableCell>
                                    <TableCell align="right" sx={hideOnPhone}>{formatMoney(totals.unpaidLeaveDeduction)}</TableCell>
                                    <TableCell align="right" sx={hideOnPhone}>{formatMoney(totals.absenceDeduction)}</TableCell>
                                    {hasOvertime && <TableCell align="right" sx={hideOnPhone}>{formatMoney(totals.overtimeAmount)}</TableCell>}
                                    <TableCell align="right">{formatMoney(totals.netSalary)}</TableCell>
                                    <TableCell colSpan={3} />
                                </TableRow>
                            )}
                            {payrolls.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={hasOvertime ? 11 : 10} align="center">
                                        No payroll records for {MONTHS[month - 1]} {year}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalElements}
                        page={page}
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={[25, 50, 100, 200]}
                        onPageChange={(_, newPage) => loadPayrolls(newPage, pageSize, true)}
                        onRowsPerPageChange={(e) => loadPayrolls(0, parseInt(e.target.value, 10), true)}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
                    />
                </TableContainer>
            )}
            {loaded && totals && totals.headcount > payrolls.length && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    The Total row covers the whole run ({totals.headcount} employees), not just this page.
                </Typography>
            )}

            <BatchPaymentDialog
                open={batchDialogOpen}
                onClose={() => setBatchDialogOpen(false)}
                selectedPayrolls={selectedBatchRows}
                onSuccess={handleBatchSuccess}
                institutionId={institutionId}
                year={year}
                month={month}
            />

            <Dialog open={emailConfirmOpen} onClose={handleCancelEmailAll} PaperProps={{ 'data-help-page': 'payroll.emailConfirm.page' } as any} maxWidth="sm" fullWidth>
                <DialogTitle>Email all payslips?</DialogTitle>
                <DialogContent dividers data-help-section="payroll.emailConfirm.section.message">
                    <Typography>
                        This will email all {totals ? `${finalizedCount} ` : ''}finalized payslip{totals && finalizedCount === 1 ? '' : 's'} for {MONTHS[month - 1]} {year}
                        {selectedInstitutionName ? ` (${selectedInstitutionName})` : ''} to the employees
                        on this run. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button data-help="payroll.emailConfirm.cancel" onClick={handleCancelEmailAll} disabled={emailingAll}>Cancel</Button>
                    <Button
                        data-help="payroll.emailConfirm.confirm"
                        onClick={handleConfirmEmailAll}
                        variant="contained"
                        color="primary"
                        disabled={emailingAll}
                    >
                        {emailingAll ? 'Sending...' : 'Email all'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={finalizeConfirmOpen} onClose={() => setFinalizeConfirmOpen(false)} PaperProps={{ 'data-help-page': 'payroll.finalizeConfirm.page' } as any} maxWidth="sm" fullWidth>
                <DialogTitle>Finalize all payrolls?</DialogTitle>
                <DialogContent dividers data-help-section="payroll.finalizeConfirm.section.message">
                    <Typography>
                        This will finalize {draftCount} draft payroll{draftCount === 1 ? '' : 's'} for{' '}
                        {MONTHS[month - 1]} {year}
                        {selectedInstitutionName ? ` (${selectedInstitutionName})` : ''}.
                        Finalized payrolls are locked and can be paid and emailed to employees.
                    </Typography>
                    {noSalaryExcluded.length > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }} data-testid="finalize-no-salary-warning">
                            {noSalaryExcluded.length} active employee{noSalaryExcluded.length === 1 ? ' has' : 's have'} no
                            active salary and {noSalaryExcluded.length === 1 ? 'is' : 'are'} not in this run. They will not be paid unless a salary is added.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="payroll.finalizeConfirm.cancel" onClick={() => setFinalizeConfirmOpen(false)}>Cancel</Button>
                    <Button data-help="payroll.finalizeConfirm.confirm" onClick={handleFinalizeAll} variant="contained" color="warning">
                        Finalize {draftCount} payroll{draftCount === 1 ? '' : 's'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={voidConfirmOpen} onClose={handleCancelVoidDraft} PaperProps={{ 'data-help-page': 'payroll.voidConfirm.page' } as any} maxWidth="sm" fullWidth>
                <DialogTitle>Void draft payroll month?</DialogTitle>
                <DialogContent dividers data-help-section="payroll.voidConfirm.section.message">
                    <Typography>
                        This will permanently void {draftCount} DRAFT payroll{draftCount === 1 ? '' : 's'} for{' '}
                        {MONTHS[month - 1]} {year}
                        {selectedInstitutionName ? ` (${selectedInstitutionName})` : ''}.
                        {finalizedCount > 0
                            ? ` The ${finalizedCount} already-FINALIZED payroll${finalizedCount === 1 ? '' : 's'} for this month will NOT be touched.`
                            : ''}
                        {' '}You can regenerate the voided employees afterward with Generate Payroll.
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button data-help="payroll.voidConfirm.cancel" onClick={handleCancelVoidDraft} disabled={voidingDraft}>Cancel</Button>
                    <Button
                        data-help="payroll.voidConfirm.confirm"
                        onClick={handleConfirmVoidDraft}
                        variant="contained"
                        color="error"
                        disabled={voidingDraft}
                    >
                        {voidingDraft ? 'Voiding...' : 'Void Draft Month'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PayrollList;
